// import UPNG from "./UPNG";

export type ImageDisplayMode =
  | "scaleToFill" // 不保持纵横比缩放图片，使图片的宽高完全拉伸至填满 image 元素
  | "aspectFit" // 保持纵横比缩放图片，使图片的长边能完全显示出来。也就是说，可以完整地将图片显示出来
  | "aspectFill" // 保持纵横比缩放图片，只保证图片的短边能完全显示出来。也就是说，图片通常只在水平或垂直方向是完整的，另一个方向将会发生截取
  | "widthFix" // 保持纵横比缩放图片，宽度完全拉伸，高度自动变化
  | "heightFix"; // 保持纵横比缩放图片，高度完全拉伸，宽度自动变化

export function computeImageDisplayRect(
  mode: ImageDisplayMode,
  sourceWidth: number,
  sourceHeight: number,
  containerWidth: number,
  containerHeight: number
): { width: number; height: number } {
  const sourceAspectRatio = sourceWidth / sourceHeight;
  let showWidth = 0;
  let showHeight = 0;
  switch (mode) {
    case "aspectFit":
      if (sourceAspectRatio >= 1) {
        showWidth = containerWidth;
        showHeight = showWidth / sourceAspectRatio;
        if (showHeight > containerHeight) {
          showHeight = containerHeight;
          showWidth = showHeight * sourceAspectRatio;
        }
      } else {
        showHeight = containerHeight;
        showWidth = showHeight * sourceAspectRatio;
        if (showWidth > containerWidth) {
          showWidth = containerWidth;
          showHeight = showWidth / sourceAspectRatio;
        }
      }
      break;
    case "aspectFill":
      if (sourceAspectRatio >= 1) {
        showHeight = containerHeight;
        showWidth = showHeight * sourceAspectRatio;
      } else {
        showWidth = containerWidth;
        showHeight = showWidth / sourceAspectRatio;
      }
      break;
    case "widthFix":
      showWidth = containerWidth;
      showHeight = showWidth / sourceAspectRatio;
      break;
    case "heightFix":
      showHeight = containerHeight;
      showWidth = showHeight * sourceAspectRatio;
      break;
    case "scaleToFill":
    default:
      showWidth = containerWidth;
      showHeight = containerHeight;
      break;
  }
  return { width: showWidth, height: showHeight };
}

export function getImageData(
  image: ImageBitmap | HTMLImageElement | HTMLVideoElement
): ImageData {
  try {
    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return ctx.getImageData(0, 0, canvas.width, canvas.height) as ImageData;
  } catch (e) {
    // Uncaught SecurityError: Failed to execute 'getImageData' on 'CanvasRenderingContext2D': The canvas has been tainted by cross-origin data.
    return new ImageData(image.width, image.height);
  }
}

/// ImageData对象转Bitmap
export const imageDataToBitmap = (imageData: ImageData) => {
  //
  // BITMAPFILEHEADER: http://msdn.microsoft.com/en-us/library/windows/desktop/dd183374(v=vs.85).aspx
  // BITMAPINFOHEADER: http://msdn.microsoft.com/en-us/library/dd183376.aspx
  //

  const biWidth = imageData.width;
  const biHeight = imageData.height;
  const biSizeImage = biWidth * biHeight * 3;
  const bfSize = biSizeImage + 54; // total header size = 54 bytes

  //
  //  typedef struct tagBITMAPFILEHEADER {
  //  	WORD bfType;
  //  	DWORD bfSize;
  //  	WORD bfReserved1;
  //  	WORD bfReserved2;
  //  	DWORD bfOffBits;
  //  } BITMAPFILEHEADER;
  //
  const BITMAPFILEHEADER = [
    // WORD bfType -- The file type signature; must be "BM"
    0x42,
    0x4d,
    // DWORD bfSize -- The size, in bytes, of the bitmap file
    bfSize & 0xff,
    (bfSize >> 8) & 0xff,
    (bfSize >> 16) & 0xff,
    (bfSize >> 24) & 0xff,
    // WORD bfReserved1 -- Reserved; must be zero
    0,
    0,
    // WORD bfReserved2 -- Reserved; must be zero
    0,
    0,
    // DWORD bfOffBits -- The offset, in bytes, from the beginning of the BITMAPFILEHEADER structure to the bitmap bits.
    54,
    0,
    0,
    0
  ];

  //
  //  typedef struct tagBITMAPINFOHEADER {
  //  	DWORD biSize;
  //  	LONG  biWidth;
  //  	LONG  biHeight;
  //  	WORD  biPlanes;
  //  	WORD  biBitCount;
  //  	DWORD biCompression;
  //  	DWORD biSizeImage;
  //  	LONG  biXPelsPerMeter;
  //  	LONG  biYPelsPerMeter;
  //  	DWORD biClrUsed;
  //  	DWORD biClrImportant;
  //  } BITMAPINFOHEADER, *PBITMAPINFOHEADER;
  //
  const BITMAPINFOHEADER = [
    // DWORD biSize -- The number of bytes required by the structure
    40,
    0,
    0,
    0,
    // LONG biWidth -- The width of the bitmap, in pixels
    biWidth & 0xff,
    (biWidth >> 8) & 0xff,
    (biWidth >> 16) & 0xff,
    (biWidth >> 24) & 0xff,
    // LONG biHeight -- The height of the bitmap, in pixels
    biHeight & 0xff,
    (biHeight >> 8) & 0xff,
    (biHeight >> 16) & 0xff,
    (biHeight >> 24) & 0xff,
    // WORD biPlanes -- The number of planes for the target device. This value must be set to 1
    1,
    0,
    // WORD biBitCount -- The number of bits-per-pixel, 24 bits-per-pixel -- the bitmap
    // has a maximum of 2^24 colors (16777216, Truecolor)
    24,
    0,
    // DWORD biCompression -- The type of compression, BI_RGB (code 0) -- uncompressed
    0,
    0,
    0,
    0,
    // DWORD biSizeImage -- The size, in bytes, of the image. This may be set to zero for BI_RGB bitmaps
    biSizeImage & 0xff,
    (biSizeImage >> 8) & 0xff,
    (biSizeImage >> 16) & 0xff,
    (biSizeImage >> 24) & 0xff,
    // LONG biXPelsPerMeter, unused
    0,
    0,
    0,
    0,
    // LONG biYPelsPerMeter, unused
    0,
    0,
    0,
    0,
    // DWORD biClrUsed, the number of color indexes of palette, unused
    0,
    0,
    0,
    0,
    // DWORD biClrImportant, unused
    0,
    0,
    0,
    0
  ];

  const iPadding = (4 - ((biWidth * 3) % 4)) % 4;
  const aImgData = imageData.data;

  let strPixelData = "";
  const biWidth4 = biWidth << 2;
  let y = biHeight;
  const fromCharCode = String.fromCharCode;

  do {
    const iOffsetY = biWidth4 * (y - 1);
    let strPixelRow = "";
    for (let x = 0; x < biWidth; x++) {
      const iOffsetX = x << 2;
      strPixelRow +=
        fromCharCode(aImgData[iOffsetY + iOffsetX + 2]) +
        fromCharCode(aImgData[iOffsetY + iOffsetX + 1]) +
        fromCharCode(aImgData[iOffsetY + iOffsetX]);
    }

    for (let c = 0; c < iPadding; c++) {
      strPixelRow += String.fromCharCode(0);
    }

    strPixelData += strPixelRow;
  } while (--y);

  function encodeData(data: string | any[]) {
    if (!window.btoa) {
      throw "btoa undefined";
    }
    let str = "";
    if (typeof data == "string") {
      str = data;
    } else {
      for (let i = 0; i < data.length; i++) {
        str += String.fromCharCode(data[i]);
      }
    }
    return btoa(str);
  }

  return (
    encodeData(BITMAPFILEHEADER.concat(BITMAPINFOHEADER)) +
    encodeData(strPixelData)
  );
};

/**
 * ImageData对象转DataURL
 */
export function imageDataToDataURL(imageData: ImageData): string {
  const { width, height } = imageData;
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  canvas.width = width;
  canvas.height = height;
  ctx.putImageData(imageData, 0, 0, 0, 0, width, height);
  const dataUrl = canvas.toDataURL("image/png"); // 可添加 quality: any 进行有损压缩
  return dataUrl;
}

/// ImageData对象转Blob
export const imageDataToBlob = (
  imageData: ImageData,
  exportImageType?: string,
  cb?: (err: any, blob: Blob | null) => void
) => {
  const { width, height } = imageData;
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  canvas.width = width;
  canvas.height = height;
  ctx.putImageData(imageData, 0, 0, 0, 0, width, height);
  const toImageType = `image/${
    exportImageType ? exportImageType.toLowerCase() : "png"
  }`;
  canvas.toBlob(
    (blob: Blob | null) => {
      cb?.(null, blob);
    },
    toImageType,
    1
  );
};

export function blobToImageData(blob: Blob, options?: ImageBitmapOptions) {
  return createImageBitmap(blob, options);
}

/// 获取图片的主要颜色值,返回表示颜色值的字符串,如rgb(red,green,blue)
export function getImageMainColor(imageData: ImageData) {
  const arrR = [];
  const arrG = [];
  const arrB = [];
  let j = 0;
  for (let i = 0, len = imageData.data.length; i < len; i += 4) {
    arrR[j] = imageData.data[i];
    arrG[j] = imageData.data[i + 1];
    arrB[j] = imageData.data[i + 2];
    j += 1;
  }
  const R = _getArryWeightValue(arrR);
  const G = _getArryWeightValue(arrG);
  const B = _getArryWeightValue(arrB);
  return "rgb(" + R + "," + G + "," + B + ")";
}
// 用于获取数组中的最高权重值 返回数组的最高权重值
function _getArryWeightValue(arr: number[]) {
  const existArr = {} as { [index: number]: number };
  const duplicationArr = [];
  const duplicationCount = [];
  for (let i = 0; i < arr.length; i++) {
    if (!existArr[arr[i]]) {
      existArr[arr[i]] = 1;
    } else {
      duplicationArr.push(arr[i]);
    }
  }
  let k = 0;
  for (let i = 0; i < duplicationArr.length; i++) {
    k = 0;
    for (let j = 0; j < duplicationArr.length; j++) {
      if (duplicationArr[i] === duplicationArr[j]) {
        k += 1;
      }
      duplicationCount[i] = k;
    }
  }
  const max = Math.max.apply(null, duplicationCount);
  let maxIndex = 0;
  for (; maxIndex < duplicationCount.length; maxIndex++) {
    if (duplicationCount[maxIndex] === max) {
      return;
    }
  }
  return duplicationArr[maxIndex];
}

/// 左右翻转ImageData
export const flipSideToSide = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[(y * width + width - x - 1) * 4];
      newImgData[startIndex + 1] = data[(y * width + width - x - 1) * 4 + 1];
      newImgData[startIndex + 2] = data[(y * width + width - x - 1) * 4 + 2];
      newImgData[startIndex + 3] = data[(y * width + width - x - 1) * 4 + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 上下翻转ImageData
export const flipUpsideDown = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[((height - y - 1) * width + x) * 4];
      newImgData[startIndex + 1] = data[((height - y - 1) * width + x) * 4 + 1];
      newImgData[startIndex + 2] = data[((height - y - 1) * width + x) * 4 + 2];
      newImgData[startIndex + 3] = data[((height - y - 1) * width + x) * 4 + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 左旋转ImageData
export const leftRotate = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const startIndex = (x * height + y) * 4;
      newImgData[startIndex] = data[(y * width + width - x - 1) * 4];
      newImgData[startIndex + 1] = data[(y * width + width - x - 1) * 4 + 1];
      newImgData[startIndex + 2] = data[(y * width + width - x - 1) * 4 + 2];
      newImgData[startIndex + 3] = data[(y * width + width - x - 1) * 4 + 3];
    }
  }
  const newImageData = new ImageData(newImgData, height, width);
  return newImageData;
};

/// 右旋转ImageData
export const rightRotate = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const startIndex = (x * height + y) * 4;
      newImgData[startIndex] = data[((height - y - 1) * width + x) * 4];
      newImgData[startIndex + 1] = data[((height - y - 1) * width + x) * 4 + 1];
      newImgData[startIndex + 2] = data[((height - y - 1) * width + x) * 4 + 2];
      newImgData[startIndex + 3] = data[((height - y - 1) * width + x) * 4 + 3];
    }
  }
  const newImageData = new ImageData(newImgData, height, width);
  return newImageData;
};

/// 灰化ImageData
export const toGrey = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      const avgColor =
        (data[startIndex] + data[startIndex + 1] + data[startIndex + 2]) / 3;
      newImgData[startIndex] = avgColor;
      newImgData[startIndex + 1] = avgColor;
      newImgData[startIndex + 2] = avgColor;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 灰化ImageData
export const toGrey2 = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      //relative luminance, humans perceive green more
      const avgColor =
        0.2126 * data[startIndex] +
        0.7152 * data[startIndex + 1] +
        0.0722 * data[startIndex + 2];
      newImgData[startIndex] = avgColor;
      newImgData[startIndex + 1] = avgColor;
      newImgData[startIndex + 2] = avgColor;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 黑白化ImageData
export const toBlackAndWhite = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      const avgColor =
        (data[startIndex] + data[startIndex + 1] + data[startIndex + 2]) / 3;
      const newColor = avgColor > 127 ? 255 : 0;
      newImgData[startIndex] = newColor;
      newImgData[startIndex + 1] = newColor;
      newImgData[startIndex + 2] = newColor;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 反相色滤镜ImageData
export const toOpposite = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = 255 - data[startIndex];
      newImgData[startIndex + 1] = 255 - data[startIndex + 1];
      newImgData[startIndex + 2] = 255 - data[startIndex + 2];
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 红色滤镜ImageData
export const toRed = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[startIndex];
      newImgData[startIndex + 1] = 0;
      newImgData[startIndex + 2] = 0;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 绿色滤镜ImageData
export const toGreen = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = 0;
      newImgData[startIndex + 1] = data[startIndex + 1];
      newImgData[startIndex + 2] = 0;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 蓝色滤镜ImageData
export const toBlue = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = 0;
      newImgData[startIndex + 1] = 0;
      newImgData[startIndex + 2] = data[startIndex + 2];
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 红绿色滤镜ImageData
export const toRedAndGreen = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[startIndex];
      newImgData[startIndex + 1] = data[startIndex + 1];
      newImgData[startIndex + 2] = 0;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 红蓝色滤镜ImageData
export const toRedAndBlue = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[startIndex];
      newImgData[startIndex + 1] = 0;
      newImgData[startIndex + 2] = data[startIndex + 1];
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 蓝绿色滤镜ImageData
export const toBlueAndGreen = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = 0;
      newImgData[startIndex + 1] = data[startIndex + 1];
      newImgData[startIndex + 2] = data[startIndex + 1];
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 红灰色滤镜ImageData
export const toRedAndGrey = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      const greyColor = (data[startIndex + 1] + data[startIndex + 2]) / 2;
      newImgData[startIndex] = data[startIndex];
      newImgData[startIndex + 1] = greyColor;
      newImgData[startIndex + 2] = greyColor;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 绿灰色滤镜ImageData
export const toGreenAndGrey = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      const greyColor = (data[startIndex] + data[startIndex + 2]) / 2;
      newImgData[startIndex] = greyColor;
      newImgData[startIndex + 1] = data[startIndex + 1];
      newImgData[startIndex + 2] = greyColor;
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 蓝灰色滤镜ImageData
export const toBlueAndGrey = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      const greyColor = (data[startIndex] + data[startIndex + 1]) / 2;
      newImgData[startIndex] = greyColor;
      newImgData[startIndex + 1] = greyColor;
      newImgData[startIndex + 2] = data[startIndex + 2];
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// 锐化ImageData
export const sharpen = (imageData: ImageData) => {
  const kernel = [-1, -1, -1, -1, 9, -1, -1, -1, -1]; // 锐化卷积核
  const newImageData = convolutionMatrix(imageData, kernel);
  return newImageData;
};

/// 边缘锐化ImageData
export const marginSharpen = (imageData: ImageData) => {
  const kernel = [-1, -1, -1, -1, 8, -1, -1, -1, -1]; // 边缘化卷积核
  const newImageData = convolutionMatrix(imageData, kernel);
  return newImageData;
};

/// JPG转PNG
export const jpgToPng = (imageData: ImageData): ImageData => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = data[startIndex];
      newImgData[startIndex + 1] = data[startIndex + 1];
      newImgData[startIndex + 2] = data[startIndex + 2];
      newImgData[startIndex + 3] = 255;
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/// PNG转JPG
export const pngToJpg = (imageData: ImageData): ImageData => {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      if (data[startIndex + 3] === 0) {
        newImgData[startIndex] = 255;
        newImgData[startIndex + 1] = 255;
        newImgData[startIndex + 2] = 255;
        newImgData[startIndex + 3] = 255;
      } else {
        const newColor = colorStacks(
          [
            data[startIndex],
            data[startIndex + 1],
            data[startIndex + 2],
            data[startIndex + 3]
          ],
          [255, 255, 255, 255]
        );
        newImgData[startIndex] = newColor[0];
        newImgData[startIndex + 1] = newColor[1];
        newImgData[startIndex + 2] = newColor[2];
        newImgData[startIndex + 3] = newColor[3];
      }
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
};

/**
 * 矩形裁剪
 * @param imageData ImageData源数据
 * @param newWidth 新图的宽度（retainOriginalSize为true时，宽度为imageData.width）
 * @param newHeight 新图的高度（retainOriginalSize为true时，高度为imageData.height）
 * @param top 新图相对于原图顶部的起始位置
 * @param left 新图相对于原图左侧的起始位置
 * @param retainOriginalSize 是否保留为原图的宽高 (宽高继承原图，多余部分用白色或透明色填充)
 * @param imageType 图片类型 (retainOriginalSize为true时必须传该值)
 * @returns ImageData
 */
export function rectClip(
  imageData: ImageData,
  newWidth: number,
  newHeight: number,
  top: number,
  left: number,
  retainOriginalSize = false,
  imageType?: string
): ImageData {
  const { data, width, height } = imageData;
  let finalLeft = left;
  let finalTop = top;
  let finalWidth = newWidth;
  let finalHeight = newHeight;
  if (finalLeft < 0) {
    finalLeft = 0;
  } else if (finalLeft > width) {
    finalLeft = width;
  }
  if (finalTop < 0) {
    finalTop = 0;
  } else if (finalTop > height) {
    finalTop = height;
  }
  if (finalWidth < 1) {
    finalWidth = 1;
  } else if (finalWidth > width) {
    finalWidth = width;
  }
  if (finalHeight < 1) {
    finalHeight = 1;
  } else if (finalHeight > height) {
    finalHeight = height;
  }
  if (finalLeft + finalWidth > width) {
    finalWidth = width - finalLeft;
  }
  if (finalTop + finalHeight > height) {
    finalHeight = height - finalTop;
  }
  // 保留原尺寸
  if (retainOriginalSize) {
    const newImgData = new Uint8ClampedArray(data.length);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const startIndex = (y * width + x) * 4;
        if (
          x >= finalLeft &&
          x <= finalLeft + finalWidth &&
          y >= finalTop &&
          y <= finalTop + finalHeight
        ) {
          newImgData[startIndex] = data[startIndex];
          newImgData[startIndex + 1] = data[startIndex + 1];
          newImgData[startIndex + 2] = data[startIndex + 2];
          newImgData[startIndex + 3] = data[startIndex + 3];
        } else {
          newImgData[startIndex] = 255;
          newImgData[startIndex + 1] = 255;
          newImgData[startIndex + 2] = 255;
          newImgData[startIndex + 3] =
            imageType && ["JPG", "JPEG"].includes(imageType.toUpperCase())
              ? 255
              : 0;
        }
      }
    }
    const newImageData = new ImageData(newImgData, width, height);
    return newImageData;
  } else {
    const newImgData = new Uint8ClampedArray(finalWidth * finalHeight * 4);
    for (let y = finalTop; y <= finalTop + finalHeight; y++) {
      for (let x = finalLeft; x <= finalLeft + finalWidth; x++) {
        const startIndex = ((y - finalTop) * finalWidth + x - finalLeft) * 4;
        const startIndex2 = (y * width + x) * 4;
        newImgData[startIndex] = data[startIndex2];
        newImgData[startIndex + 1] = data[startIndex2 + 1];
        newImgData[startIndex + 2] = data[startIndex2 + 2];
        newImgData[startIndex + 3] = data[startIndex2 + 3];
      }
    }
    const newImageData = new ImageData(newImgData, finalWidth, finalHeight);
    return newImageData;
  }
}

/**
 * 圆角裁剪
 * @param imageData ImageData源数据
 * @param borderRadius 圆角半径
 * @param imageType 图片类型
 * @param jpgToPNG 是否为JPG转成PNG
 * @returns ImageData
 */
export function radiusClip(
  imageData: ImageData,
  borderRadius: number,
  imageType: string,
  jpgToPNG?: boolean
): ImageData {
  const { data, width, height } = imageData;
  let finalRadius = borderRadius;
  if (finalRadius > Math.floor(width / 2)) {
    finalRadius = Math.floor(width / 2);
  }
  if (finalRadius > Math.floor(height / 2)) {
    finalRadius = Math.floor(height / 2);
  }
  if (finalRadius < 0) {
    finalRadius = 0;
  }
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      let l = -1;
      if (x < finalRadius) {
        if (y < finalRadius) {
          l = Math.sqrt(
            Math.pow(finalRadius - x - 1, 2) + Math.pow(finalRadius - y - 1, 2)
          );
        } else if (y > height - finalRadius - 1) {
          l = Math.sqrt(
            Math.pow(finalRadius - x - 1, 2) +
              Math.pow(y - (height - finalRadius), 2)
          );
        }
      } else if (x > width - finalRadius - 1) {
        if (y < finalRadius) {
          l = Math.sqrt(
            Math.pow(x - (width - finalRadius), 2) +
              Math.pow(finalRadius - y - 1, 2)
          );
        } else if (y > height - finalRadius - 1) {
          l = Math.sqrt(
            Math.pow(x - (width - finalRadius), 2) +
              Math.pow(y - (height - finalRadius), 2)
          );
        }
      }
      if (Math.round(l) > finalRadius) {
        newImgData[startIndex] = 255;
        newImgData[startIndex + 1] = 255;
        newImgData[startIndex + 2] = 255;
        newImgData[startIndex + 3] =
          jpgToPNG || imageType.toUpperCase() === "PNG" ? 0 : 255;
      } else {
        newImgData[startIndex] = data[startIndex];
        newImgData[startIndex + 1] = data[startIndex + 1];
        newImgData[startIndex + 2] = data[startIndex + 2];
        newImgData[startIndex + 3] =
          jpgToPNG || imageType.toUpperCase() === "PNG"
            ? data[startIndex + 3]
            : 255;
      }
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/**
 * 图片缩放，修改尺寸
 * @param imgUrl - 图片地址
 * @param width - sourceWidth
 * @param height - sourceHeight
 * @param newWidth - targetWidth
 * @param newHeight - targetHeight
 * @param maxWidthHeight - 最大长度
 * @returns
 */
export function changeSize(
  imgUrl: string,
  width: number,
  height: number,
  newWidth: number,
  newHeight: number,
  maxWidthHeight = 10000
): ImageData {
  const img = new Image();
  img.src = imgUrl;
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  canvas.width = maxWidthHeight;
  canvas.height = maxWidthHeight;
  ctx.drawImage(img, 0, 0, width, height, 0, 0, newWidth, newHeight);
  const newImageData = ctx.getImageData(0, 0, newWidth, newHeight) as ImageData;
  return newImageData;
}

/// 修改亮度
export function changeBrightness(
  imageData: ImageData,
  changeNum: number
): ImageData {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      newImgData[startIndex] = Math.max(
        Math.min(data[startIndex] + changeNum, 255),
        0
      );
      newImgData[startIndex + 1] = Math.max(
        Math.min(data[startIndex + 1] + changeNum, 255),
        0
      );
      newImgData[startIndex + 2] = Math.max(
        Math.min(data[startIndex + 2] + changeNum, 255),
        0
      );
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/// 修改透明度
export function changeDiaphaneity(
  imageData: ImageData,
  value: number,
  fixedDiaphaneity = false
): ImageData {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      if (data[startIndex + 3] === 0) {
        newImgData[startIndex] = 255;
        newImgData[startIndex + 1] = 255;
        newImgData[startIndex + 2] = 255;
      } else {
        newImgData[startIndex] = data[startIndex];
        newImgData[startIndex + 1] = data[startIndex + 1];
        newImgData[startIndex + 2] = data[startIndex + 2];
      }
      let a = data[startIndex + 3];
      if (fixedDiaphaneity) {
        a = Math.max(Math.min(Math.floor(255 * value), 255), 0);
      } else {
        a = Math.max(Math.min(Math.floor(a + 255 * value), 255), 0);
      }
      newImgData[startIndex + 3] = a;
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/// 添加水印
export function addWatermark(
  imageData: ImageData,
  watermarkImageData: ImageData,
  top: number,
  left: number
): ImageData {
  const { data, width, height } = imageData;
  const {
    data: watermarkData,
    width: watermarkWidth,
    height: watermarkHeight
  } = watermarkImageData;
  let finalTop = top;
  let finalLeft = left;
  if (top + watermarkHeight > height) {
    finalTop = height - watermarkHeight;
  }
  if (left + watermarkWidth > width) {
    finalLeft = width - watermarkWidth;
  }
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      if (
        x < finalLeft ||
        x > finalLeft + watermarkWidth ||
        y <= finalTop ||
        y >= finalTop + watermarkHeight
      ) {
        newImgData[startIndex] = data[startIndex];
        newImgData[startIndex + 1] = data[startIndex + 1];
        newImgData[startIndex + 2] = data[startIndex + 2];
        newImgData[startIndex + 3] = data[startIndex + 3];
      } else {
        const watermarkStartIndex =
          (y * width +
            x -
            ((finalTop + 1) * width +
              (y - (finalTop + 1)) * (width - watermarkWidth)) +
            watermarkWidth -
            finalLeft -
            1) *
          4;
        const aboveColor: [number, number, number, number] = [
          watermarkData[watermarkStartIndex],
          watermarkData[watermarkStartIndex + 1],
          watermarkData[watermarkStartIndex + 2],
          watermarkData[watermarkStartIndex + 3]
        ];
        const belowColor: [number, number, number, number] = [
          data[startIndex],
          data[startIndex + 1],
          data[startIndex + 2],
          data[startIndex + 3]
        ];
        const finalColor = colorStacks(aboveColor, belowColor);
        newImgData[startIndex] = finalColor[0];
        newImgData[startIndex + 1] = finalColor[1];
        newImgData[startIndex + 2] = finalColor[2];
        newImgData[startIndex + 3] = finalColor[3];
      }
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/**
 * 打马赛克
 * @param imageData ImageData源数据
 * @param width 马赛克区域的宽度
 * @param height 马赛克区域的高度
 * @param top 马赛克相对于顶部的起始位置
 * @param left 马塞克相对于左侧的起始位置
 * @param mosaicSize 马塞克颗粒的大小
 * @returns ImageData
 */
export function mosaic(
  imageData: ImageData,
  mosaicWidth: number,
  mosaicHeight: number,
  top: number,
  left: number,
  mosaicSize = 10
): ImageData {
  const { data, width, height } = imageData;
  let finalLeft = left;
  let finalTop = top;
  let finalWidth = mosaicWidth;
  let finalHeight = mosaicHeight;
  if (finalLeft < 0) {
    finalLeft = 0;
  } else if (finalLeft > width) {
    finalLeft = width;
  }
  if (finalTop < 0) {
    finalTop = 0;
  } else if (finalTop > height) {
    finalTop = height;
  }
  if (finalWidth < 1) {
    finalWidth = 1;
  } else if (finalWidth > width) {
    finalWidth = width;
  }
  if (finalHeight < 1) {
    finalHeight = 1;
  } else if (finalHeight > height) {
    finalHeight = height;
  }
  if (finalLeft + finalWidth > width) {
    finalWidth = width - finalLeft;
  }
  if (finalTop + finalHeight > height) {
    finalHeight = height - finalTop;
  }

  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      if (
        x >= finalLeft &&
        x <= finalLeft + finalWidth &&
        y >= finalTop &&
        y <= finalTop + finalHeight
      ) {
        const boxXIndex = Math.floor((x - finalLeft) / mosaicSize);
        const boxYIndex = Math.floor((y - finalTop) / mosaicSize);
        let mosaicBoxColorX = Math.floor(
          mosaicSize * (boxXIndex + 1) - mosaicSize / 2 + finalLeft
        );
        let mosaicBoxColorY = Math.floor(
          mosaicSize * (boxYIndex + 1) - mosaicSize / 2 + finalTop
        );
        if (mosaicBoxColorX > width - 1) {
          mosaicBoxColorX = width - 1;
        }
        if (mosaicBoxColorY > height - 1) {
          mosaicBoxColorY = height - 1;
        }
        const mosaicBoxColorIndex =
          (mosaicBoxColorY * width + mosaicBoxColorX) * 4;
        newImgData[startIndex] = data[mosaicBoxColorIndex];
        newImgData[startIndex + 1] = data[mosaicBoxColorIndex + 1];
        newImgData[startIndex + 2] = data[mosaicBoxColorIndex + 2];
        newImgData[startIndex + 3] = data[mosaicBoxColorIndex + 3];
      } else {
        newImgData[startIndex] = data[startIndex];
        newImgData[startIndex + 1] = data[startIndex + 1];
        newImgData[startIndex + 2] = data[startIndex + 2];
        newImgData[startIndex + 3] = data[startIndex + 3];
      }
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/// 图片压缩
// export const compression = async (
//   imageUrl: string | ImageData,
//   width: number,
//   height: number,
//   imageType: string,
//   compressionDegree: number,
//   cb: (blob: Blob | null) => void
// ) => {
//   const degree = compressionDegree / 100;
//   if (["JPG", "JPEG"].includes(imageType.toUpperCase())) {
//     const img = new Image();
//     img.src = imageUrl as string;
//     const canvas = document.createElement("canvas") as HTMLCanvasElement;
//     const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
//     canvas.width = width;
//     canvas.height = height;
//     ctx.drawImage(img, 0, 0, width, height);
//     canvas.toBlob(
//       (blob: Blob | null) => {
//         cb(blob);
//       },
//       `image/${imageType.toLowerCase()}`,
//       degree
//     );
//   } else {
//     const bit = Math.floor(degree * 256);
//     const png = UPNG.encode(
//       [(imageUrl as ImageData).data.buffer],
//       width,
//       height,
//       bit
//     );
//     const blob = new Blob([png]);
//     cb && cb(blob);
//   }
// };

/// 根据AI识别的人像数据算出界面中需要显示的最终数据
export function getImageDataByAIData(
  originalImageData: ImageData,
  AIImageData: ImageData,
  options: {
    backgroundColor: { r: number; g: number; b: number; a: number };
    changeIntoColor: { r: number; g: number; b: number; a: number };
  } = {
    backgroundColor: { r: 0, g: 0, b: 0, a: 255 },
    changeIntoColor: { r: 0, g: 0, b: 0, a: 0 }
  }
): ImageData {
  if (AIImageData && options) {
    const { data, width, height } = originalImageData;
    const { data: AIData } = AIImageData;
    const newImgData = new Uint8ClampedArray(data.length);
    const { backgroundColor, changeIntoColor } = options;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const startIndex = (y * width + x) * 4;
        const r = AIData[startIndex];
        const g = AIData[startIndex + 1];
        const b = AIData[startIndex + 2];
        const a = AIData[startIndex + 3];
        if (
          r === backgroundColor.r &&
          g === backgroundColor.g &&
          b === backgroundColor.b &&
          a === backgroundColor.a &&
          changeIntoColor.a !== 0
        ) {
          newImgData[startIndex] = changeIntoColor.r;
          newImgData[startIndex + 1] = changeIntoColor.g;
          newImgData[startIndex + 2] = changeIntoColor.b;
          newImgData[startIndex + 3] = changeIntoColor.a;
        } else {
          newImgData[startIndex] = data[startIndex];
          newImgData[startIndex + 1] = data[startIndex + 1];
          newImgData[startIndex + 2] = data[startIndex + 2];
          newImgData[startIndex + 3] = data[startIndex + 3];
        }
      }
    }
    const newImageData = new ImageData(newImgData, width, height);
    return newImageData;
  }
  return originalImageData;
}

/// 卷积算法
function convolutionMatrix(imageData: ImageData, kernel: number[]): ImageData {
  const { data, width, height } = imageData;
  const newImgData = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIndex = (y * width + x) * 4;
      for (let i = 0; i < 3; i++) {
        const index = startIndex + i;
        if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
          newImgData[index] = data[index];
        } else {
          newImgData[index] =
            kernel[0] * data[index - width * 4 - 4] +
            kernel[1] * data[index - width * 4] +
            kernel[2] * data[index - width * 4 + 4] +
            kernel[3] * data[index - 4] +
            kernel[4] * data[index] +
            kernel[5] * data[index + 4] +
            kernel[6] * data[index + width * 4 - 4] +
            kernel[7] * data[index + width * 4] +
            kernel[8] * data[index + width * 4 + 4];
        }
      }
      newImgData[startIndex + 3] = data[startIndex + 3];
    }
  }
  const newImageData = new ImageData(newImgData, width, height);
  return newImageData;
}

/// 颜色叠加算法
function colorStacks(
  aboveColor: [number, number, number, number],
  belowColor: [number, number, number, number]
) {
  if (
    aboveColor &&
    aboveColor.length === 4 &&
    belowColor &&
    belowColor.length === 4
  ) {
    const aboveA = aboveColor[3];
    const belowA = belowColor[3];
    if (aboveA === 255 || belowA === 0) {
      return aboveColor;
    } else if (aboveA === 0) {
      return belowColor;
    } else {
      const aboveDiaphaneity = aboveA / 255;
      const belowDiaphaneity = belowA / 255;
      const newColorR = Math.max(
        Math.min(
          Math.floor(
            aboveColor[0] * aboveDiaphaneity +
              belowColor[0] * belowDiaphaneity * (1 - aboveDiaphaneity)
          ),
          255
        ),
        0
      );
      const newColorG = Math.max(
        Math.min(
          Math.floor(
            aboveColor[1] * aboveDiaphaneity +
              belowColor[1] * belowDiaphaneity * (1 - aboveDiaphaneity)
          ),
          255
        ),
        0
      );
      const newColorB = Math.max(
        Math.min(
          Math.floor(
            aboveColor[2] * aboveDiaphaneity +
              belowColor[2] * belowDiaphaneity * (1 - aboveDiaphaneity)
          ),
          255
        ),
        0
      );
      const newColorA = Math.max(
        Math.min(
          Math.floor(
            (aboveDiaphaneity + belowDiaphaneity * (1 - aboveDiaphaneity)) * 255
          ),
          255
        ),
        0
      );
      return [newColorR, newColorG, newColorB, newColorA];
    }
  }
  return [255, 255, 255, 255];
}
